/**
 * calorieTrackerEngine.js — Re Leve IA (Etapa 5)
 * Acompanhamento calórico INFORMATIVO baseado em kits Re Leve.
 *
 * LIMITES DE SEGURANÇA — NUNCA:
 * - Calcular meta calórica ideal
 * - Dizer "você deve consumir X calorias"
 * - Prometer emagrecimento
 * - Prescrever dieta
 * Sempre exibir: "Acompanhamento informativo. Não substitui nutricionista."
 */

import {
  getStoredCaloriePlan,
  setStoredCaloriePlan,
  getStoredCalorieLogs,
  addCalorieLog,
  clearCalorieLogs,
  clearCaloriePlan,
} from './localStorageEngine';
import { hasCalorieConsent } from './consentEngine';

export const DISCLAIMER = 'Acompanhamento informativo. Não substitui nutricionista.';

// ── Estimativas calóricas padrão por tipo de refeição ─────────────────────────

export const CALORIE_ESTIMATES = {
  almoco: 450,
  jantar: 320,
  snack: 180,
  bebida: 120,
  sobremesa: 220,
  sopa: 320,
  low_carb: 380,
};

// ── Planos calóricos por kit ───────────────────────────────────────────────────

const KIT_PLANS = {
  'KIT_001': {
    kitName: 'Kit Dia Leve',
    meals: [
      { type: 'almoco',  label: 'Almoço',  estimatedKcal: 450 },
      { type: 'jantar',  label: 'Jantar',  estimatedKcal: 320 },
      { type: 'snack',   label: 'Snack',   estimatedKcal: 180 },
      { type: 'bebida',  label: 'Bebida',  estimatedKcal: 120 },
    ],
    totalEstimatedKcal: 1070,
  },
  'KIT_002': {
    kitName: 'Kit Dia Controle',
    meals: [
      { type: 'almoco',  label: 'Almoço',   estimatedKcal: 420 },
      { type: 'jantar',  label: 'Jantar',   estimatedKcal: 320 },
      { type: 'snack',   label: 'Snack',    estimatedKcal: 160 },
    ],
    totalEstimatedKcal: 900,
  },
  'KIT_003': {
    kitName: 'Kit Dia Performance',
    meals: [
      { type: 'almoco',  label: 'Almoço',  estimatedKcal: 550 },
      { type: 'jantar',  label: 'Jantar',  estimatedKcal: 450 },
      { type: 'snack',   label: 'Snack',   estimatedKcal: 220 },
      { type: 'bebida',  label: 'Bebida',  estimatedKcal: 150 },
    ],
    totalEstimatedKcal: 1370,
  },
  'KIT_004': {
    kitName: 'Semana Econômica',
    meals: [
      { type: 'almoco',  label: 'Almoço (×5)', estimatedKcal: 450, multiplier: 5 },
    ],
    totalEstimatedKcal: 2250,
    isWeekly: true,
    weeklyNote: 'Plano semanal: 5 almoços estimados.',
  },
  'KIT_005': {
    kitName: 'Semana Útil Resolvida',
    meals: [
      { type: 'almoco',  label: 'Almoço (×5)',  estimatedKcal: 450, multiplier: 5 },
      { type: 'jantar',  label: 'Jantar (×5)',  estimatedKcal: 350, multiplier: 5 },
      { type: 'snack',   label: 'Snack (×5)',   estimatedKcal: 180, multiplier: 5 },
      { type: 'bebida',  label: 'Bebida (×5)',  estimatedKcal: 120, multiplier: 5 },
    ],
    totalEstimatedKcal: 5500,
    isWeekly: true,
    weeklyNote: 'Plano semanal: 5 dias completos.',
  },
  'KIT_006': {
    kitName: 'Semana Completa Re Leve',
    meals: [
      { type: 'almoco',    label: 'Almoço (×7)',    estimatedKcal: 450, multiplier: 7 },
      { type: 'jantar',    label: 'Jantar (×7)',    estimatedKcal: 350, multiplier: 7 },
      { type: 'snack',     label: 'Snack (×7)',     estimatedKcal: 180, multiplier: 7 },
      { type: 'bebida',    label: 'Bebida (×7)',    estimatedKcal: 120, multiplier: 7 },
      { type: 'sobremesa', label: 'Sobremesa (×3)', estimatedKcal: 220, multiplier: 3 },
    ],
    totalEstimatedKcal: 8570,
    isWeekly: true,
    weeklyNote: 'Plano semanal: 7 dias completos.',
  },
  // KIT_007 e KIT_008: coletivos — não criar plano individual automaticamente
  'KIT_007': { isCollective: true, kitName: 'Família Organizada' },
  'KIT_008': { isCollective: true, kitName: 'Escritório Saudável' },
};

export const COLLECTIVE_MESSAGE = 'Como pode envolver mais de uma pessoa, posso organizar a compra, mas o acompanhamento individual precisa ser separado por usuário.';
export const WORKPLACE_MESSAGE = 'Para compras coletivas, o acompanhamento calórico deve ser individual e autorizado por cada pessoa.';

// ── Criar plano calórico ───────────────────────────────────────────────────────

export function createCaloriePlan({ clienteId, kit }) {
  if (!clienteId || !hasCalorieConsent(clienteId)) return null;
  if (!kit?.id) return null;

  const template = KIT_PLANS[kit.id];
  if (!template) return null;
  if (template.isCollective) {
    return {
      isCollective: true,
      kitName: template.kitName,
      message: kit.id === 'KIT_008' ? WORKPLACE_MESSAGE : COLLECTIVE_MESSAGE,
      disclaimer: DISCLAIMER,
    };
  }

  const plan = {
    clienteId,
    kitId: kit.id,
    kitName: template.kitName,
    meals: template.meals,
    totalEstimatedKcal: template.totalEstimatedKcal,
    isWeekly: template.isWeekly || false,
    weeklyNote: template.weeklyNote || null,
    disclaimer: DISCLAIMER,
    active: true,
  };

  setStoredCaloriePlan(clienteId, plan);
  return plan;
}

export function getCaloriePlan(clienteId) {
  if (!clienteId) return null;
  return getStoredCaloriePlan(clienteId);
}

// ── Registrar refeição ─────────────────────────────────────────────────────────

export function registerMeal({ clienteId, mealType, itemName, estimatedCalories, kitId }) {
  if (!clienteId || !hasCalorieConsent(clienteId)) return null;

  const kcal = estimatedCalories ?? CALORIE_ESTIMATES[mealType] ?? 0;
  return addCalorieLog(clienteId, {
    mealType,
    itemName: itemName || mealType,
    estimatedKcal: kcal,
    kitId: kitId || null,
    disclaimer: DISCLAIMER,
  });
}

// ── Resumo diário ──────────────────────────────────────────────────────────────

export function getDailyCalorieSummary(clienteId) {
  if (!clienteId) return null;
  const logs = getStoredCalorieLogs(clienteId);
  const today = new Date().toISOString().slice(0, 10);
  const todayLogs = logs.filter(l => (l.timestamp || '').startsWith(today));

  return {
    date: today,
    totalKcal: todayLogs.reduce((sum, l) => sum + (l.estimatedKcal || 0), 0),
    meals: todayLogs,
    mealCount: todayLogs.length,
    disclaimer: DISCLAIMER,
  };
}

// ── Resumo semanal ─────────────────────────────────────────────────────────────

export function getWeeklyCalorieSummary(clienteId) {
  if (!clienteId) return null;
  const logs = getStoredCalorieLogs(clienteId);

  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const weekLogs = logs.filter(l => {
    const ts = new Date(l.timestamp || 0);
    return ts >= sevenDaysAgo;
  });

  const totalKcal = weekLogs.reduce((sum, l) => sum + (l.estimatedKcal || 0), 0);
  const daysWithData = new Set(weekLogs.map(l => (l.timestamp || '').slice(0, 10))).size;

  return {
    totalKcal,
    daysWithData,
    averageKcalPerDay: daysWithData > 0 ? Math.round(totalKcal / daysWithData) : 0,
    mealCount: weekLogs.length,
    disclaimer: DISCLAIMER,
  };
}

// ── Sugestão de próxima refeição ───────────────────────────────────────────────

export function suggestNextMeal(clienteId) {
  const plan = getCaloriePlan(clienteId);
  const daily = getDailyCalorieSummary(clienteId);

  if (!plan || plan.isCollective) return null;

  const registeredTypes = new Set((daily?.meals || []).map(m => m.mealType));
  const pending = (plan.meals || []).filter(m => !registeredTypes.has(m.type));

  if (pending.length === 0) {
    return {
      suggestion: 'Todas as refeições do plano de hoje foram registradas.',
      disclaimer: DISCLAIMER,
    };
  }

  const next = pending[0];
  return {
    nextMealType: next.type,
    nextMealLabel: next.label,
    estimatedKcal: next.estimatedKcal,
    suggestion: `Próxima refeição sugerida: ${next.label} (~${next.estimatedKcal} kcal estimados, informativo).`,
    disclaimer: DISCLAIMER,
  };
}

// ── Criar plano a partir de kit confirmado (Etapa 6) ─────────────────────────

export function createPlanFromConfirmedKit({ clienteId, kit, source }) {
  if (!clienteId) return { success: false, reason: 'clienteId ausente' };
  if (!hasCalorieConsent(clienteId)) {
    return { success: false, reason: 'sem_consentimento', needsConsent: true };
  }
  if (!kit?.id && !kit?.kitId) {
    return { success: false, reason: 'kit_invalido' };
  }

  const kitId = kit.id || kit.kitId;
  const template = KIT_PLANS[kitId];
  if (!template) return { success: false, reason: 'kit_sem_plano' };

  if (template.isCollective) {
    return {
      success: false,
      isCollective: true,
      kitName: template.kitName,
      reason: 'kit_coletivo',
      message: kitId === 'KIT_008' ? WORKPLACE_MESSAGE : COLLECTIVE_MESSAGE,
    };
  }

  const plan = {
    clienteId,
    kitId,
    kitName: template.kitName,
    meals: template.meals.map(m => ({ ...m, status: 'planned' })),
    totalEstimatedKcal: template.totalEstimatedKcal,
    isWeekly: template.isWeekly || false,
    weeklyNote: template.weeklyNote || null,
    source: source || 'kit_confirmed',
    disclaimer: DISCLAIMER,
    active: true,
    createdAt: new Date().toISOString(),
  };

  setStoredCaloriePlan(clienteId, plan);
  return { success: true, plan };
}

// ── Registrar refeições planejadas por kit ────────────────────────────────────

export function registerPlannedMealsForKit({ clienteId, kit, mode }) {
  if (!clienteId || !hasCalorieConsent(clienteId)) return null;
  if (mode !== 'confirmed') return null;

  const kitId = kit?.id || kit?.kitId;
  if (!kitId) return null;

  const template = KIT_PLANS[kitId];
  if (!template || template.isCollective) return null;

  // Registrar apenas as refeições do dia (não toda a semana)
  const dayMeals = template.meals.filter(m => !m.multiplier || m.multiplier === 1);
  const logs = [];

  for (const meal of dayMeals) {
    const log = addCalorieLog(clienteId, {
      mealType: meal.type,
      itemName: meal.label,
      estimatedKcal: meal.estimatedKcal,
      kitId,
      status: 'planned',
      source: 'kit_confirmed',
      disclaimer: DISCLAIMER,
    });
    logs.push(log);
  }

  return { registered: logs.length, logs, disclaimer: DISCLAIMER };
}

// ── Resetar acompanhamento ─────────────────────────────────────────────────────

export function resetCalorieTracking(clienteId) {
  clearCalorieLogs(clienteId);
  clearCaloriePlan(clienteId);
}
